import { useEffect, useState } from 'react';
import { HLSSelectProps, HLSVideoTrack, settingControllerKey } from '../../PlayerModels';
import { VideoJsPlayer } from 'video.js';
import { CustomOption, CustomOptionWrapper, SelectingItemMainWrapper, SettingPanelMultiSelectItem } from '../../styles';
import { normalizeQualities, handleRangeQualities } from '../../PlayerServices';
import { PanelSettingHeader, PanelSettingLayer } from '../../styles/Panel';
import { Box } from '../../../../styles';
import { IconQualityChange } from '../../../../icons';
import UsePlayerProps from '../../../../Store/usePlayerProps';
import { __t } from '../../../../i18n';

export default function ControlBarQualityLevelPanel({ player, handleSettingItemClick, activeSetting }: {
  player: VideoJsPlayer,
  handleSettingItemClick: (controllerKey: settingControllerKey) => void
  activeSetting: string | null
}) {
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { sources } = playerGlobalState;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHLSBitrate, setSelectedHLSBitrate] = useState<string | number>('auto');
  const [selectedQualityLabel, setSelectedQualityLabel] = useState<string | number>('auto');
  // const [selectedMP4Quality, setSelectedMP4Quality] = useState<number>(1080);
  // const { tech } = useTech();
  const [hlsQualities, setHlsQualities] = useState<HLSVideoTrack[]>([]);

  useEffect(() => {
    if (!player) return ;
    updateHlsQualities();

    const qualityLevels = player.qualityLevels();

    qualityLevels.on('change', updateHlsQualities);

    return () => {
      qualityLevels.off('change', updateHlsQualities);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player]);

  const updateHlsQualities = () => {
    const qualityList = player.qualityLevels().levels_;
    const qualities: HLSVideoTrack[] = [
      {
        id: 'auto',
        label: 'auto',
        bitrate: 'auto',
        enabled: true,
        height: 'Auto',
        width: 'auto',
        quality: 'auto',
      },
    ];
    qualityList.forEach((item) => {
      qualities.push({
        id: item.id,
        label: item.label,
        bitrate: item.bitrate,
        enabled: false,
        height: item.height,
        width: item.width,
        quality: `${handleRangeQualities(item.height)}`,
      });
    });
    const sortedQualities: HLSVideoTrack[] = normalizeQualities(qualities);
    setHlsQualities(sortedQualities);
  };

  useEffect(() => {
    if (sources?.videos) {
      setSelectedHLSBitrate('auto');
      setSelectedQualityLabel('auto');
    }
  }, [sources?.videos]);

  // useEffect(() => {
  //   if (tech === 'mp4') {
  //     sources?.videos && setSelectedMP4Quality(480);
  //   } else {
  //     sources?.videos && setSelectedHLSQuality('auto');
  //   }
  // }, [sources?.videos, tech]);

  const toggleMenu = (e, key) => {
    e.stopPropagation();
    handleSettingItemClick(key);
    setIsOpen(!isOpen);
  };
  // const handleMP4QualityChange = (player: VideoJsPlayer, source: MP4VideoTrack) => {
  //   const currentTime = player.currentTime();
  //   // const paused = player.paused();
  //
  //   // player.one('loadeddata', function () {
  //   //   player.hasStarted(true);
  //   //   player.currentTime(currentTime);
  //   //   if (!paused) player.play();
  //   // });
  //
  //   player.src({
  //     src: source?.src,
  //     type: source.type,
  //   });
  //   player.currentTime(currentTime);
  // };
  const handleHLSQualityChange = (selectedQuality: string | number, player: VideoJsPlayer) => {
    const qualityLevels = player.qualityLevels();
    if (selectedQuality !== 'auto') {
      qualityLevels.levels_.length &&
      qualityLevels.levels_.forEach((elem) => {
        if (elem.bitrate === Number(selectedQuality)) {
          elem.enabled_(true);
        } else {
          elem.enabled_(false);
        }
      });
    } else {
      qualityLevels.levels_.length &&
      qualityLevels.levels_.forEach((elem) => {
        elem.enabled_(true);
      });
    }
  };

  return (
    <>
      {!activeSetting &&
        <SettingPanelMultiSelectItem icon={<IconQualityChange />} label="player.quality"
          onClick={(e) => toggleMenu(e, 'quality')}
          value={selectedQualityLabel === 'auto' ? __t('player.auto') : Number(selectedQualityLabel) > 1080 && Number(selectedQualityLabel) < 1440 ? 'HQ' : Number(selectedQualityLabel) >= 1440 ? 'HQ' : selectedQualityLabel.toString()} />}
      {activeSetting === 'quality' && <PanelSettingLayer>
        {hlsQualities && (
          <HLSSelect
            returnAction={(e) => toggleMenu(e, null)}
            options={hlsQualities}
            onChange={(quality: string, bitrate: string | number, player: VideoJsPlayer) => {
              if (bitrate !== selectedHLSBitrate) {
                setSelectedHLSBitrate(bitrate);
                setSelectedQualityLabel(quality);
                handleHLSQualityChange(bitrate, player);
              }
              setIsOpen(false);
            }}
            value={selectedHLSBitrate}
            player={player}
          />
        )}
      </PanelSettingLayer>}
    </>
  );
}
// const MP4Select = ({ value, options, onChange, returnAction }: MP4SelectProps) => {
//   return (
//     <Box>
//       <PanelSettingHeader label={'player.selected_quality'} handleClick={(e) => returnAction(e)} />
//       <SelectingItemMainWrapper>
//         {options.map((item: MP4VideoTrack) => (
//           <CustomOptionWrapper key={item.id} onClick={(e: any) => {
//             onChange(item.quality, item);
//             returnAction(e);
//           }}>
//             <CustomOption selected={item.quality === value}>
//               {item.quality}
//             </CustomOption>
//           </CustomOptionWrapper>
//         ))}
//       </SelectingItemMainWrapper>
//     </Box>
//   );
// };

const HLSSelect = ({ value, options, onChange, player, returnAction }: HLSSelectProps) => {
  return (
    <Box>
      <PanelSettingHeader label={'player.selected_quality'} handleClick={(e) => returnAction(e)} />
      <SelectingItemMainWrapper>
        {options.map((item: HLSVideoTrack) => (
          <CustomOptionWrapper key={item.id} onClick={(e: any) => {
            onChange(String(item.quality), String(item.quality) === 'auto' ? 'auto' : item.bitrate, player);
            returnAction(e);
          }}>
            <CustomOption selected={item.bitrate === value}>
              {item.quality === 'auto' ? __t('player.auto') : Number(item.quality) > 1080 && Number(item.quality) < 1440 ? 'HQ' : Number(item.quality) >= 1440 ? 'HQ' : item.quality}
            </CustomOption>
          </CustomOptionWrapper>
        ))}
      </SelectingItemMainWrapper>
    </Box>
  );
};
